import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  useTheme,
  Avatar,
  Divider,
  Chip,
  IconButton,
  Fade,
  Link,
} from '@mui/material';
import {
  School,
  Work,
  People as Family,
  FlightTakeoff as Flight,
  Business,
  Assignment,
  CheckCircle,
  Star,
  Timeline,
  Language,
  Security,
  SupportAgent,
  Speed,
  EmojiEvents,
  Article,
  Gavel,
  VerifiedUser,
  Groups,
  KeyboardArrowDown,
  LocationOn,
  AccessTime,
  Phone,
  Email,
  AccountBalance,
  School as Education,
  HomeWork,
  Public,
  LocalAirport,
  BusinessCenter,
  Facebook,
  LinkedIn,
  WhatsApp,
  YouTube,
  ArrowForward,
} from '@mui/icons-material';

// Import a TikTok icon component
const TikTokIcon: React.FC = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 448 512" 
    fill="currentColor" 
    style={{ width: '1em', height: '1em' }}
  >
    <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/>
  </svg>
);

const services = [
  {
    title: 'Student Visas',
    icon: School,
    description: 'Expert guidance for students pursuing education in the UK.',
    link: '/services/student-visa',
    color: '#4CAF50',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
    hoverGradient: 'linear-gradient(315deg, #4CAF50 0%, #81C784 100%)',
  },
  {
    title: 'Work Visas',
    icon: Work,
    description: 'Professional support for employment-based immigration.',
    link: '/services/work-visa',
    color: '#2196F3',
    gradient: 'linear-gradient(135deg, #2196F3 0%, #64B5F6 100%)',
    hoverGradient: 'linear-gradient(315deg, #2196F3 0%, #64B5F6 100%)',
  },
  {
    title: 'Family Visas',
    icon: Family,
    description: 'Reunite with your loved ones in the UK.',
    link: '/services/family-visa',
    color: '#E91E63',
    gradient: 'linear-gradient(135deg, #E91E63 0%, #F48FB1 100%)',
    hoverGradient: 'linear-gradient(315deg, #E91E63 0%, #F48FB1 100%)',
  },
  {
    title: 'Visitor Visas',
    icon: Flight,
    description: 'Assistance for business, family, or leisure visits.',
    link: '/services/visitor-visa',
    color: '#9C27B0',
    gradient: 'linear-gradient(135deg, #9C27B0 0%, #CE93D8 100%)',
    hoverGradient: 'linear-gradient(315deg, #9C27B0 0%, #CE93D8 100%)',
  },
  {
    title: 'Business Immigration',
    icon: Business,
    description: 'Solutions for entrepreneurs and investors.',
    link: '/services/business-visa',
    color: '#FF9800',
    gradient: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
    hoverGradient: 'linear-gradient(315deg, #FF9800 0%, #FFB74D 100%)',
  },
  {
    title: 'Appeals & Reviews',
    icon: Assignment,
    description: 'Expert handling of visa refusals and appeals.',
    link: '/services/appeals',
    color: '#F44336',
    gradient: 'linear-gradient(135deg, #F44336 0%, #EF9A9A 100%)',
    hoverGradient: 'linear-gradient(315deg, #F44336 0%, #EF9A9A 100%)',
  },
];

const features = [
  {
    icon: Security,
    title: 'Expert Legal Guidance',
    description: 'Our team of experienced immigration lawyers provides comprehensive legal support for all UK visa categories.',
    color: '#2196F3',
    gradient: 'linear-gradient(135deg, #2196F3 0%, #64B5F6 100%)',
    hoverGradient: 'linear-gradient(315deg, #2196F3 0%, #64B5F6 100%)',
  },
  {
    icon: Speed,
    title: 'Fast Processing',
    description: 'We ensure efficient handling of your application with our streamlined processes and expert knowledge.',
    color: '#4CAF50',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
    hoverGradient: 'linear-gradient(315deg, #4CAF50 0%, #81C784 100%)',
  },
  {
    icon: Language,
    title: 'Multilingual Support',
    description: 'We offer services in English, Urdu, Punjabi, Pothwari, and Hindi for clear communication.',
    color: '#9C27B0',
    gradient: 'linear-gradient(135deg, #9C27B0 0%, #CE93D8 100%)',
    hoverGradient: 'linear-gradient(315deg, #9C27B0 0%, #CE93D8 100%)',
  },
  {
    icon: SupportAgent,
    title: '24/7 Support',
    description: 'Get assistance whenever you need it with our dedicated support team.',
    color: '#FF9800',
    gradient: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
    hoverGradient: 'linear-gradient(315deg, #FF9800 0%, #FFB74D 100%)',
  },
];

const successStats = [
  {
    number: '95%',
    label: 'Success Rate',
  },
  {
    number: '1000+',
    label: 'Successful Applications',
  },
  {
    number: '10+',
    label: 'Years Experience',
  },
  {
    number: '24/7',
    label: 'Client Support',
  },
];

const whyChooseUs = [
  'Experienced immigration lawyers',
  'Personalized consultation',
  'Comprehensive documentation support',
  'High success rate',
  'Transparent process',
  'Competitive fees',
];

// Add new constants for news and accreditations
const latestNews = [
  {
    title: 'UK Immigration Rule Changes 2024',
    date: 'March 15, 2024',
    description: 'Important updates to UK immigration policies and how they might affect your application.',
    link: '/news/uk-immigration-changes-2024',
  },
  {
    title: 'New Student Visa Guidelines',
    date: 'March 10, 2024',
    description: 'Updated guidelines for international students planning to study in the UK.',
    link: '/news/student-visa-guidelines',
  },
  {
    title: 'Skilled Worker Visa Updates',
    date: 'March 5, 2024',
    description: 'Recent changes to the Skilled Worker visa category and salary requirements.',
    link: '/news/skilled-worker-updates',
  },
];

// Update the accreditations constant with additional styling properties
const accreditations = [
  {
    name: 'OISC Registered',
    icon: VerifiedUser,
    description: 'Office of the Immigration Services Commissioner',
    color: '#2196F3',
    gradient: 'linear-gradient(135deg, #2196F3 0%, #64B5F6 100%)',
    hoverGradient: 'linear-gradient(315deg, #2196F3 0%, #64B5F6 100%)',
  },
  {
    name: 'Law Society Member',
    icon: Gavel,
    description: 'Recognized member of the Law Society',
    color: '#4CAF50',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
    hoverGradient: 'linear-gradient(315deg, #4CAF50 0%, #81C784 100%)',
  },
  {
    name: 'ILPA Member',
    icon: Groups,
    description: 'Immigration Law Practitioners\' Association',
    color: '#9C27B0',
    gradient: 'linear-gradient(135deg, #9C27B0 0%, #CE93D8 100%)',
    hoverGradient: 'linear-gradient(315deg, #9C27B0 0%, #CE93D8 100%)',
  },
];

const quickLinks = [
  { icon: LocationOn, text: '1 Turner Road Atif Centre High Court Lahore' },
  { icon: AccessTime, text: '24/7 Support' },
  { icon: Phone, text: 'contact@chishtyukimmigrationlaw.info' },
];

const illustrationIcons = [
  { Icon: AccountBalance, size: 64, color: 'rgba(255,255,255,0.9)', top: '10%', left: '20%', delay: 0 },
  { Icon: Education, size: 48, color: 'rgba(255,255,255,0.7)', top: '25%', left: '10%', delay: 0.1 },
  { Icon: HomeWork, size: 56, color: 'rgba(255,255,255,0.8)', top: '40%', left: '25%', delay: 0.2 },
  { Icon: Public, size: 72, color: 'rgba(255,255,255,0.95)', top: '15%', left: '60%', delay: 0.3 },
  { Icon: LocalAirport, size: 48, color: 'rgba(255,255,255,0.7)', top: '35%', left: '70%', delay: 0.4 },
  { Icon: BusinessCenter, size: 52, color: 'rgba(255,255,255,0.8)', top: '60%', left: '65%', delay: 0.5 },
];

// Add social media links constant
const socialMediaLinks = [
  { icon: Facebook, url: 'https://www.facebook.com/hassan.subhani.chishty.2024/', color: '#1877F2' },
  { icon: YouTube, url: 'https://www.youtube.com/channel/UCGEyd0O_LfosEwlnnGh1zBA', color: '#FF0000' },
  { icon: LinkedIn, url: 'https://www.linkedin.com/posts/hassan-chishty-4985b233a_ukimmigration-sponsorlicence-skilledworkervisa-activity-7315123868468170752-Y1yX?utm_source=share&utm_medium=member_android&rcm=ACoAAFiua40B_UeLVaC8DBBU0-Z6HTgMbI4DHmA', color: '#0A66C2' },
  { icon: TikTokIcon, url: 'https://www.tiktok.com/@ukvisaexpert24?_t=ZS-8vTdqA0GGKl&_r=1', color: '#000000' },
          { icon: WhatsApp, url: 'https://wa.me/923206258495', color: '#25D366' },
];

// Update the company name constant
const companyName = "Chishty UK Immigration Law & Visa Advisor";

// Update contact information
const contactInfo = {
  address: "1 Turner Road Atif Centre High Court Lahore",
  email: "contact@chishtyukimmigrationlaw.info",
  phone: "+92 320 6258495",
};

const Home = () => {
  const theme = useTheme();

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Floating Social Media Sidebar */}
      <Box
        sx={{
          position: 'fixed',
          right: 20,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1000,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          gap: 1.5,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: 1.5,
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)',
          '& > a': {
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateX(-5px)',
            },
          },
        }}
      >
        {socialMediaLinks.map((social, index) => {
          const SocialIcon = social.icon;
          return (
            <IconButton
              key={index}
              component="a"
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: social.color,
                padding: 1.2,
                '&:hover': {
                  backgroundColor: `${social.color}15`,
                },
                '& .MuiSvgIcon-root, & svg': {
                  fontSize: '1.5rem',
                },
              }}
            >
              <SocialIcon />
            </IconButton>
          );
        })}
      </Box>

      {/* Mobile Social Media Links */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
          display: { xs: 'block', md: 'none' },
          py: 1.5,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          {socialMediaLinks.map((social, index) => {
            const SocialIcon = social.icon;
            return (
              <IconButton
                key={index}
                component="a"
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                size="medium"
                sx={{
                  color: social.color,
                  padding: 1.5,
                  '&:hover': {
                    backgroundColor: `${social.color}15`,
                  },
                  '& .MuiSvgIcon-root, & svg': {
                    fontSize: '1.8rem',
                  },
                }}
              >
                <SocialIcon />
              </IconButton>
            );
          })}
        </Stack>
      </Box>

      {/* Enhanced Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 12, md: 16 },
          pb: { xs: 20, md: 24 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url(/images/uk-map-pattern.png) no-repeat center center',
            backgroundSize: 'cover',
            opacity: 0.1,
            animation: 'pulse 4s ease-in-out infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            right: '-10%',
            width: '50%',
            height: '140%',
            background: `linear-gradient(135deg, ${theme.palette.secondary.main}20 0%, ${theme.palette.secondary.light}20 100%)`,
            transform: 'rotate(-12deg) translateY(-50%)',
            borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
            animation: 'morphBackground 15s ease-in-out infinite',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ position: 'relative' }}>
                <Chip
                  label="Trusted Immigration Services"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    mb: 3,
                    '& .MuiChip-label': {
                      px: 2,
                      py: 0.5,
                      fontSize: '0.9rem',
                      fontWeight: 500,
                    },
                  }}
                />
                <Typography
                  variant="h1"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.2,
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    animation: 'fadeInUp 0.8s ease-out',
                    mb: 3,
                  }}
                >
                  Your Trusted Partner for{' '}
                  <Box
                    component="span"
                    sx={{
                      color: theme.palette.secondary.light,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '0.1em',
                        left: 0,
                        width: '100%',
                        height: '0.1em',
                        backgroundColor: theme.palette.secondary.light,
                        opacity: 0.3,
                      },
                    }}
                  >
                    UK Immigration
                  </Box>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    maxWidth: 600,
                    animation: 'fadeInUp 0.8s ease-out 0.2s',
                    animationFillMode: 'both',
                    lineHeight: 1.6,
                  }}
                >
                  Expert legal services for all your UK visa and immigration needs. Start your journey with confidence and professional guidance.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{
                    animation: 'fadeInUp 0.8s ease-out 0.4s',
                    animationFillMode: 'both',
                    mb: 6,
                  }}
                >
                  <Button
                    component={RouterLink}
                    to="/consultation"
                    variant="contained"
                    color="secondary"
                    size="large"
                    endIcon={<KeyboardArrowDown />}
                    sx={{
                      px: 4,
                      py: 1.8,
                      fontSize: '1.1rem',
                      borderRadius: '30px',
                      boxShadow: '0 4px 14px rgba(255, 255, 255, 0.25)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    Free Consultation
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/services"
                    variant="outlined"
                    color="inherit"
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.8,
                      fontSize: '1.1rem',
                      borderRadius: '30px',
                      borderWidth: '2px',
                      borderColor: 'rgba(255,255,255,0.5)',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Our Services
                  </Button>
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={3}
                  sx={{
                    animation: 'fadeInUp 0.8s ease-out 0.6s',
                    animationFillMode: 'both',
                  }}
                >
                  {quickLinks.map((link, index) => {
                    const IconComponent = link.icon;
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          opacity: 0.9,
                          mb: { xs: 1, sm: 0 },
                        }}
                      >
                        <IconComponent sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            wordBreak: 'break-word',
                          }}
                        >
                          {link.text}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'relative',
                height: '500px',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  width: '100%',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '400px',
                    height: '400px',
                    background: `radial-gradient(circle at center, ${theme.palette.secondary.main}30 0%, transparent 70%)`,
                    borderRadius: '50%',
                    animation: 'pulse 4s ease-in-out infinite',
                  },
                }}
              >
                {illustrationIcons.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'absolute',
                      top: item.top,
                      left: item.left,
                      transform: 'translate(-50%, -50%)',
                      animation: `float 6s ease-in-out infinite ${item.delay}s, fadeIn 0.8s ease-out ${item.delay}s forwards`,
                      opacity: 0,
                    }}
                  >
                    <item.Icon
                      sx={{
                        fontSize: item.size,
                        color: item.color,
                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
                      }}
                    />
                  </Box>
                ))}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '200px',
                    height: '200px',
                    border: '2px solid rgba(255,255,255,0.1)',
                    borderRadius: '50%',
                    animation: 'rotate 20s linear infinite',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-2px',
                      left: '-2px',
                      right: '-2px',
                      bottom: '-2px',
                      border: '2px solid rgba(255,255,255,0.05)',
                      borderRadius: '50%',
                      animation: 'rotate 10s linear infinite reverse',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '-20px',
                      left: '-20px',
                      right: '-20px',
                      bottom: '-20px',
                      border: '2px solid rgba(255,255,255,0.03)',
                      borderRadius: '50%',
                      animation: 'rotate 15s linear infinite',
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ mt: -8, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={3}>
          {successStats.map((stat) => (
            <Grid item xs={6} md={3} key={stat.label}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  height: '100%',
                  backgroundColor: 'white',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
                elevation={2}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 1,
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Box 
        sx={{ 
          bgcolor: 'background.paper',
          py: { xs: 10, md: 15 },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.main}40 50%, transparent 100%)`,
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              variant="h2"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px',
                  height: '4px',
                  borderRadius: '2px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                },
              }}
            >
              Our Services
            </Typography>
            <Typography
              variant="h5"
              align="center"
              color="textSecondary"
              paragraph
              sx={{ 
                mt: 4,
                mb: 2,
                maxWidth: 800,
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                opacity: 0.9,
              }}
            >
              Comprehensive immigration solutions tailored to your needs
            </Typography>
          </Box>

          <Grid 
            container 
            spacing={{ xs: 3, md: 4 }} 
            sx={{ position: 'relative' }}
          >
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  key={index}
                  sx={{
                    animation: `fadeInUp 0.8s ease-out ${index * 0.2}s`,
                    animationFillMode: 'both',
                  }}
                >
                  <Paper
                    sx={{
                      p: { xs: 3, md: 4 },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 4,
                      background: '#ffffff',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                      border: '1px solid rgba(0,0,0,0.05)',
                      '&:hover': {
                        transform: 'translateY(-12px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
                        '& .service-icon': {
                          transform: 'scale(1.1) rotate(10deg)',
                          background: service.hoverGradient,
                        },
                        '& .service-icon-bg': {
                          opacity: 0.15,
                          transform: 'scale(1.2) rotate(-10deg)',
                        },
                        '& .service-content': {
                          transform: 'translateY(-5px)',
                        },
                        '& .service-button': {
                          background: service.hoverGradient,
                          transform: 'translateY(-2px)',
                        },
                      },
                    }}
                  >
                    <Box
                      className="service-icon-bg"
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: 200, md: 240 },
                        height: { xs: 200, md: 240 },
                        borderRadius: '50%',
                        background: service.gradient,
                        opacity: 0.08,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    />
                    <Box
                      className="service-icon"
                      sx={{
                        width: { xs: 80, md: 100 },
                        height: { xs: 80, md: 100 },
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: service.gradient,
                        margin: '0 auto 24px',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 8px 16px ${service.color}30`,
                        animation: 'float 4s ease-in-out infinite',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <IconComponent 
                        sx={{ 
                          fontSize: { xs: 40, md: 48 },
                          color: 'white',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                        }} 
                      />
                    </Box>
                    <Box 
                      className="service-content"
                      sx={{
                        flex: 1,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          color: service.color,
                          mb: 2,
                          fontSize: { xs: '1.25rem', md: '1.5rem' },
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography
                        color="textSecondary"
                        paragraph
                        sx={{
                          fontSize: { xs: '0.9rem', md: '1rem' },
                          lineHeight: 1.6,
                          mb: 3,
                          opacity: 0.85,
                        }}
                      >
                        {service.description}
                      </Typography>
                    </Box>
                    <Button
                      className="service-button"
                      component={RouterLink}
                      to={service.link}
                      variant="contained"
                      sx={{
                        mt: 'auto',
                        background: service.gradient,
                        color: 'white',
                        borderRadius: '25px',
                        py: 1.5,
                        px: 3,
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        fontWeight: 600,
                        textTransform: 'none',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 4px 12px ${service.color}30`,
                        '&:hover': {
                          boxShadow: `0 6px 16px ${service.color}40`,
                        },
                      }}
                    >
                      Learn More
                      <ArrowForward sx={{ ml: 1, fontSize: '1.2rem' }} />
                    </Button>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        sx={{ 
          bgcolor: 'background.default',
          py: { xs: 10, md: 15 },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.main}40 50%, transparent 100%)`,
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              variant="h2"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px',
                  height: '4px',
                  borderRadius: '2px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                },
              }}
            >
              Why Choose Us
            </Typography>
            <Typography
              variant="h5"
              align="center"
              color="textSecondary"
              paragraph
              sx={{ 
                mt: 4,
                mb: 2,
                maxWidth: 800,
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                opacity: 0.9,
              }}
            >
              Experience excellence in UK immigration services
            </Typography>
          </Box>

          <Grid 
            container 
            spacing={{ xs: 3, md: 4 }} 
            sx={{ position: 'relative' }}
          >
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={3} 
                  key={index}
                  sx={{
                    animation: `fadeInUp 0.8s ease-out ${index * 0.2}s`,
                    animationFillMode: 'both',
                  }}
                >
                  <Paper
                    sx={{
                      p: { xs: 3, md: 4 },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 4,
                      background: '#ffffff',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                      border: '1px solid rgba(0,0,0,0.05)',
                      '&:hover': {
                        transform: 'translateY(-12px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
                        '& .feature-icon': {
                          transform: 'scale(1.1) rotate(10deg)',
                          background: feature.hoverGradient,
                        },
                        '& .feature-icon-bg': {
                          opacity: 0.15,
                          transform: 'scale(1.2) rotate(-10deg)',
                        },
                        '& .feature-content': {
                          transform: 'translateY(-5px)',
                        },
                      },
                    }}
                  >
                    <Box
                      className="feature-icon-bg"
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: 200, md: 240 },
                        height: { xs: 200, md: 240 },
                        borderRadius: '50%',
                        background: feature.gradient,
                        opacity: 0.08,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    />
                    <Box
                      className="feature-icon"
                      sx={{
                        width: { xs: 80, md: 100 },
                        height: { xs: 80, md: 100 },
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: feature.gradient,
                        margin: '0 auto 24px',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 8px 16px ${feature.color}30`,
                        animation: 'float 4s ease-in-out infinite',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <IconComponent 
                        sx={{ 
                          fontSize: { xs: 40, md: 48 },
                          color: 'white',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                        }} 
                      />
                    </Box>
                    <Box 
                      className="feature-content"
                      sx={{
                        flex: 1,
                        textAlign: 'center',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          color: feature.color,
                          mb: 2,
                          fontSize: { xs: '1.25rem', md: '1.5rem' },
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        color="textSecondary"
                        sx={{
                          fontSize: { xs: '0.9rem', md: '1rem' },
                          lineHeight: 1.6,
                          opacity: 0.85,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Process Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
              Our Process
            </Typography>
            <Typography variant="h5" paragraph color="textSecondary">
              We make the visa application process simple and straightforward
            </Typography>
            <List>
              {[
                'Initial Free Consultation',
                'Document Assessment',
                'Application Preparation',
                'Submission & Tracking',
                'Post-Submission Support',
              ].map((step, index) => (
                <ListItem key={step}>
                  <ListItemIcon>
                    <Timeline sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={step}
                    secondary={`Step ${index + 1}`}
                    primaryTypographyProps={{
                      variant: 'h6',
                      gutterBottom: true,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                  What Sets Us Apart
                </Typography>
                <List>
                  {whyChooseUs.map((item) => (
                    <ListItem key={item}>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Testimonials Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 8 }}
          >
            Client Success Stories
          </Typography>
          <Grid container spacing={4}>
            {[
              {
                name: 'Sarah Johnson',
                role: 'Student Visa',
                content: 'The team made my student visa application process incredibly smooth. Their expertise and guidance were invaluable.',
              },
              {
                name: 'Mohammed Ali',
                role: 'Work Visa',
                content: 'Professional service from start to finish. They helped me secure my skilled worker visa efficiently.',
              },
              {
                name: 'Priya Patel',
                role: 'Family Visa',
                content: 'Thanks to their support, I was able to reunite with my family in the UK. Excellent service!',
              },
            ].map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 3,
                  }}
                >
                  <CardContent>
                    <Star sx={{ color: theme.palette.primary.main, mb: 2 }} />
                    <Typography
                      variant="body1"
                      paragraph
                      sx={{ fontStyle: 'italic', mb: 3 }}
                    >
                      "{testimonial.content}"
                    </Typography>
                    <Typography variant="h6">{testimonial.name}</Typography>
                    <Typography color="textSecondary">
                      {testimonial.role}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Latest News Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography
          variant="h2"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 2,
          }}
        >
          Latest Immigration News
        </Typography>
        <Typography
          variant="h5"
          align="center"
          color="textSecondary"
          paragraph
          sx={{ mb: 8, maxWidth: 800, mx: 'auto' }}
        >
          Stay updated with the latest UK immigration news and policy changes
        </Typography>
        <Grid container spacing={4}>
          {latestNews.map((news, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Article color="primary" sx={{ mr: 1 }} />
                    <Chip
                      label={news.date}
                      size="small"
                      sx={{
                        backgroundColor: `${theme.palette.primary.main}15`,
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {news.title}
                  </Typography>
                  <Typography color="textSecondary" paragraph>
                    {news.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2 }}>
                  <Button
                    component={RouterLink}
                    to={news.link}
                    color="primary"
                    endIcon={<Article />}
                  >
                    Read More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Enhanced CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at center, rgba(255,255,255,0.1) 0%, transparent 70%)',
            animation: 'pulse 4s ease-in-out infinite',
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={8} textAlign="center">
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                  variant="h2"
                  gutterBottom
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    lineHeight: 1.2,
                    mb: 3,
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    '& span': {
                      color: theme.palette.secondary.light,
                    },
                  }}
                >
                  Ready to Start Your <span>UK Immigration Journey?</span>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    maxWidth: 600,
                    mx: 'auto',
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  Take the first step towards your future with our expert guidance
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={3}
                  justifyContent="center"
                  sx={{ mb: 6 }}
                >
                  <Button
                    component={RouterLink}
                    to="/consultation"
                    variant="contained"
                    color="secondary"
                    size="large"
                    endIcon={<KeyboardArrowDown />}
                    sx={{
                      px: { xs: 4, md: 6 },
                      py: 2,
                      fontSize: { xs: '1rem', md: '1.2rem' },
                      fontWeight: 600,
                      borderRadius: '50px',
                      background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
                      boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
                      },
                    }}
                  >
                    Book Free Consultation
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/contact"
                    variant="contained"
                    size="large"
                    sx={{
                      px: { xs: 4, md: 6 },
                      py: 2,
                      fontSize: { xs: '1rem', md: '1.2rem' },
                      fontWeight: 600,
                      borderRadius: '50px',
                      backgroundColor: 'white',
                      color: 'white',
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Contact Us
                  </Button>
                </Stack>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={12} sm={10} md={8}>
                    <Paper
                      sx={{
                        p: 3,
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        textAlign: 'left',
                        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.2)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <LocationOn sx={{ 
                            color: 'white',
                            fontSize: '1.5rem',
                          }} />
                          <Typography sx={{ 
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            color: 'white',
                            fontWeight: 500,
                          }}>
                            {contactInfo.address}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Email sx={{ 
                            color: 'white',
                            fontSize: '1.5rem',
                          }} />
                          <Typography sx={{ 
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            color: 'white',
                            fontWeight: 500,
                          }}>
                            {contactInfo.email}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Update keyframe animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slide {
            from { background-position: 0 0; }
            to { background-position: 100% 100%; }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.1; transform: scale(1); }
            50% { opacity: 0.2; transform: scale(1.05); }
          }
          @keyframes morphBackground {
            0%, 100% { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
            25% { border-radius: 40% 60% 60% 40% / 40% 40% 60% 60%; }
            50% { border-radius: 30% 70% 50% 50% / 50% 50% 70% 30%; }
            75% { border-radius: 50% 50% 70% 30% / 30% 70% 50% 50%; }
          }
          @keyframes float {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
          }
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default Home; 